import { useEffect, useRef, useState } from 'react';
import {
  Content,
  Grid,
  Column,
  Heading,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  Form,
  FormGroup,
  Toggle,
  InlineNotification,
  Tag,
  Button,
  Loading,
  ProgressBar,
} from '@carbon/react';
import {
  Ai, PlayFilled, CheckmarkFilled, ErrorFilled, Renew,
} from '@carbon/icons-react';
import { apiClient } from '@/lib/api/client';
import { useAuth } from '@/lib/auth/auth-context';
import AIConfigurationPanel from '@/components/ai/AIConfigurationPanel';
import DoclingConfigurationPanel from '@/components/settings/DoclingConfigurationPanel';
import { FontSizeControl } from '@/components/settings/FontSizeControl';
import {
  meEmbeddingsApi,
  type ReembedJob,
  type EmbeddingStats,
  type ReembedTarget,
} from '@/lib/api/admin.api';

/**
 * SettingsPage Component
 *
 * User settings and configuration page.
 * Tabs: AI Configuration · Embeddings · Beta Features
 */
export default function SettingsPage() {
  const { user, refreshUser } = useAuth();
  const [betaError, setBetaError] = useState<string | null>(null);
  const [isSavingBeta, setIsSavingBeta] = useState(false);

  // ── Embeddings tab state ───────────────────────────────────────────────────
  const [embedStats, setEmbedStats] = useState<EmbeddingStats | null>(null);
  const [embedStatsLoading, setEmbedStatsLoading] = useState(false);
  const [embedJobs, setEmbedJobs] = useState<ReembedJob[]>([]);
  const [embedError, setEmbedError] = useState<string | null>(null);
  const [launching, setLaunching] = useState<ReembedTarget | null>(null);
  const embedPollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    void loadEmbedStats();
    void loadEmbedJobs();
  }, []);

  // Poll while any job is running
  useEffect(() => {
    const hasRunning = embedJobs.some((j) => j.status === 'running');
    if (hasRunning && !embedPollRef.current) {
      embedPollRef.current = setInterval(() => void loadEmbedJobs(), 3000);
    } else if (!hasRunning && embedPollRef.current) {
      clearInterval(embedPollRef.current);
      embedPollRef.current = null;
      void loadEmbedStats(); // refresh stats once jobs settle
    }
    return () => {
      if (embedPollRef.current) {
        clearInterval(embedPollRef.current);
        embedPollRef.current = null;
      }
    };
  }, [embedJobs]);

  async function loadEmbedStats() {
    setEmbedStatsLoading(true);
    try {
      const data = await meEmbeddingsApi.getStats();
      setEmbedStats(data);
    } catch (err: any) {
      console.warn('[SettingsPage] Failed to fetch embedding stats:', err);
    } finally {
      setEmbedStatsLoading(false);
    }
  }

  async function loadEmbedJobs() {
    try {
      const jobs = await meEmbeddingsApi.listJobs();
      setEmbedJobs(jobs);
    } catch (err: any) {
      console.warn('[SettingsPage] Failed to fetch re-embed jobs:', err);
    }
  }

  async function handleStartReembed(target: ReembedTarget) {
    setLaunching(target);
    setEmbedError(null);
    try {
      const job = await meEmbeddingsApi.startReembedJob(target);
      setEmbedJobs((prev) => [job, ...prev]);
    } catch (err: any) {
      setEmbedError(err.message || `Failed to start ${target} re-embedding job`);
    } finally {
      setLaunching(null);
    }
  }

  /**
   * Toggle the user's beta opt-in.
   */
  const handleToggleBeta = async (enabled: boolean) => {
    setBetaError(null);
    setIsSavingBeta(true);
    try {
      await apiClient.put('/api/auth/me/beta', { beta_features_enabled: enabled });
      await refreshUser();
    } catch (err: any) {
      setBetaError(err?.message || 'Failed to update beta preference');
      await refreshUser().catch(() => {});
    } finally {
      setIsSavingBeta(false);
    }
  };

  return (
    <Content className="settings-page">
      <Grid fullWidth>
        <Column lg={16} md={8} sm={4}>
          <div className="settings-header">
            <Heading>Settings</Heading>
            <p className="settings-subtitle">
              Manage your AI providers and application settings
            </p>
          </div>
        </Column>

        {/* Appearance (A7): global font size. */}
        <Column lg={16} md={8} sm={4}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '1rem',
              padding: '1rem 0',
              marginBottom: '0.5rem',
              borderBottom: '1px solid var(--cds-border-subtle-01)',
            }}
          >
            <FontSizeControl />
            <span style={{ color: 'var(--cds-text-secondary)', fontSize: '0.75rem' }}>
              Scales the whole interface. Saved on this device.
            </span>
          </div>
        </Column>

        <Column lg={16} md={8} sm={4}>
          <Tabs>
            <TabList aria-label="Settings tabs">
              <Tab>AI Configuration</Tab>
              <Tab>Document Conversion</Tab>
              <Tab>Embeddings</Tab>
              <Tab>Beta Features</Tab>
            </TabList>

            <TabPanels>
              {/* AI Configuration Tab */}
              <TabPanel>
                <AIConfigurationPanel />
              </TabPanel>

              {/* Document Conversion Tab (Docling SaaS per-user key) */}
              <TabPanel>
                <div style={{ maxWidth: 860, paddingTop: '1.5rem' }}>
                  <DoclingConfigurationPanel />
                </div>
              </TabPanel>

              {/* Embeddings Tab */}
              <TabPanel>
                <div style={{ maxWidth: 860, paddingTop: '1.5rem' }}>
                  <h2 style={{ fontSize: '1.125rem', fontWeight: 600, marginBottom: '0.25rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <Ai size={20} />
                    Embedding Status
                  </h2>
                  <p style={{ color: 'var(--cds-text-secondary)', fontSize: '0.875rem', marginBottom: '1.5rem', maxWidth: 640 }}>
                    Embeddings power semantic search and Knowledge Explorer matching.
                    If some are missing (e.g. because the AI service was temporarily unavailable
                    during indexing), you can regenerate them here without re-uploading your documents.
                  </p>

                  {embedError && (
                    <InlineNotification
                      kind="error"
                      lowContrast
                      title="Error"
                      subtitle={embedError}
                      onClose={() => setEmbedError(null)}
                      style={{ marginBottom: '1rem' }}
                    />
                  )}

                  {/* Per-layer stat cards */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
                    <SettingsEmbedCard
                      label="Knowledge Units (L1)"
                      description="Short text chunks embedded directly from document content."
                      stats={embedStats?.ku_l1 ?? null}
                      loading={embedStatsLoading}
                      target="ku_l1"
                      launching={launching}
                      onLaunch={handleStartReembed}
                    />
                    <SettingsEmbedCard
                      label="Summaries & Captions (L2)"
                      description="AI summaries for long text, and VLM captions for images. Required for image KU matching."
                      stats={embedStats?.ku_l2 ?? null}
                      loading={embedStatsLoading}
                      target="ku_l2"
                      launching={launching}
                      onLaunch={handleStartReembed}
                    />
                    <SettingsEmbedCard
                      label="Ontology Nodes"
                      description="Concept node embeddings used for Knowledge Explorer populate."
                      stats={embedStats?.ontology_nodes ?? null}
                      loading={embedStatsLoading}
                      target="ontology_nodes"
                      launching={launching}
                      onLaunch={handleStartReembed}
                    />
                  </div>

                  {/* Refresh stats button */}
                  <Button
                    kind="ghost"
                    size="sm"
                    renderIcon={Renew}
                    onClick={() => void loadEmbedStats()}
                    style={{ marginBottom: '2rem' }}
                  >
                    Refresh stats
                  </Button>

                  {/* Job history */}
                  {embedJobs.length > 0 && (
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
                        <span style={{ fontWeight: 600, fontSize: '0.875rem' }}>Recent Jobs</span>
                        <button
                          onClick={() => void loadEmbedJobs()}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--cds-text-secondary)', display: 'flex', alignItems: 'center' }}
                          title="Refresh job list"
                        >
                          <Renew size={16} />
                        </button>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                        {embedJobs.map((job) => (
                          <SettingsJobRow key={job.id} job={job} />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </TabPanel>

              {/* Beta Features Tab */}
              <TabPanel>
                <Form>
                  <FormGroup
                    legendText={
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}>
                        Beta features
                        <Tag type="warm-gray" size="sm">Beta</Tag>
                      </span>
                    }
                  >
                    <p style={{ color: 'var(--cds-text-secondary)', marginBottom: '1rem', maxWidth: 640 }}>
                      Beta features are experimental capabilities still under development. They may
                      change, behave unpredictably, or produce incorrect results. Access is granted
                      by an administrator; you can then opt in or out at any time.
                    </p>

                    {!user ? (
                      <InlineNotification
                        kind="info"
                        lowContrast
                        hideCloseButton
                        title="Sign in required"
                        subtitle="Sign in to manage your beta preferences."
                      />
                    ) : !user.beta_allowed ? (
                      <InlineNotification
                        kind="info"
                        lowContrast
                        hideCloseButton
                        title="Beta access not granted"
                        subtitle="Your administrator has not enabled beta access for your account. Contact them to request access."
                      />
                    ) : null}

                    <Toggle
                      id="beta-features-enabled"
                      labelText="Enable beta features"
                      labelA="Disabled"
                      labelB="Enabled"
                      toggled={!!user?.beta_features_enabled}
                      disabled={!user || !user.beta_allowed || isSavingBeta}
                      onToggle={(checked: boolean) => {
                        void handleToggleBeta(checked);
                      }}
                    />

                    <p
                      className="cds--label-description"
                      style={{ marginTop: '0.5rem' }}
                    >
                      Currently includes: <strong>Start from Knowledge Base</strong> — generate an
                      ontology from uploaded documents.
                    </p>

                    {betaError && (
                      <InlineNotification
                        kind="error"
                        lowContrast
                        title="Update failed"
                        subtitle={betaError}
                        onClose={() => setBetaError(null)}
                        style={{ marginTop: '1rem' }}
                      />
                    )}
                  </FormGroup>
                </Form>
              </TabPanel>
            </TabPanels>
          </Tabs>
        </Column>
      </Grid>
    </Content>
  );
}

// ─── Sub-components ───────────────────────────────────────────────────────────

interface SettingsEmbedCardProps {
  label: string;
  description: string;
  stats: { missing: number; total: number } | null;
  loading: boolean;
  target: ReembedTarget;
  launching: ReembedTarget | null;
  onLaunch: (t: ReembedTarget) => void;
}

function SettingsEmbedCard({ label, description, stats, loading, target, launching, onLaunch }: SettingsEmbedCardProps) {
  const missing = stats?.missing ?? 0;
  const total = stats?.total ?? 0;
  const present = total - missing;
  const pct = total > 0 ? Math.round((present / total) * 100) : 100;
  const allGood = stats !== null && missing === 0;

  return (
    <div style={{
      background: 'var(--cds-layer)',
      border: `1px solid ${allGood ? 'var(--cds-support-success)' : 'var(--cds-border-subtle)'}`,
      padding: '1rem',
      display: 'flex',
      flexDirection: 'column',
      gap: '0.75rem',
    }}>
      <div>
        <div style={{ fontWeight: 600, fontSize: '0.875rem', marginBottom: '0.25rem' }}>{label}</div>
        <div style={{ fontSize: '0.75rem', color: 'var(--cds-text-secondary)' }}>{description}</div>
      </div>

      {loading ? (
        <div style={{ fontSize: '0.75rem', color: 'var(--cds-text-secondary)' }}>Loading…</div>
      ) : stats ? (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', marginBottom: '0.25rem' }}>
            <span style={{ color: allGood ? 'var(--cds-support-success)' : 'var(--cds-text-error)' }}>
              {allGood ? '✓ All embedded' : `${missing.toLocaleString()} missing`}
            </span>
            <span style={{ color: 'var(--cds-text-secondary)' }}>{present.toLocaleString()} / {total.toLocaleString()}</span>
          </div>
          <ProgressBar
            value={pct}
            max={100}
            size="small"
            status={allGood ? 'finished' : 'active'}
            label=""
            hideLabel
          />
        </div>
      ) : (
        <div style={{ fontSize: '0.75rem', color: 'var(--cds-text-secondary)' }}>No data yet</div>
      )}

      <Button
        kind={allGood ? 'ghost' : 'primary'}
        size="sm"
        renderIcon={PlayFilled}
        disabled={launching !== null}
        onClick={() => onLaunch(target)}
      >
        {launching === target ? 'Starting…' : allGood ? 'Re-run anyway' : 'Backfill now'}
      </Button>
    </div>
  );
}

function SettingsJobRow({ job }: { job: ReembedJob }) {
  const label: Record<ReembedTarget, string> = {
    ku_l1: 'KU L1',
    ku_l2: 'L2 Summaries',
    ontology_nodes: 'Ontology Nodes',
  };
  const pct = job.total > 0 ? Math.round(((job.embedded + job.failed) / job.total) * 100) : 0;

  return (
    <div style={{
      background: 'var(--cds-layer)',
      border: '1px solid var(--cds-border-subtle)',
      padding: '0.75rem 1rem',
      display: 'flex',
      alignItems: 'center',
      gap: '1rem',
      fontSize: '0.8125rem',
    }}>
      <span style={{ flexShrink: 0 }}>
        {job.status === 'running' && <Loading small withOverlay={false} style={{ width: 16, height: 16 }} />}
        {job.status === 'completed' && <CheckmarkFilled size={16} style={{ color: 'var(--cds-support-success)' }} />}
        {job.status === 'failed' && <ErrorFilled size={16} style={{ color: 'var(--cds-support-error)' }} />}
      </span>
      <div style={{ flexShrink: 0, minWidth: 120 }}>
        <div style={{ fontWeight: 600 }}>{label[job.target]}</div>
        <div style={{ color: 'var(--cds-text-secondary)', fontSize: '0.75rem' }}>{new Date(job.startedAt).toLocaleTimeString()}</div>
      </div>
      {job.status !== 'failed' && job.total > 0 ? (
        <div style={{ flex: 1 }}>
          <ProgressBar
            value={pct}
            max={100}
            size="small"
            status={job.status === 'completed' ? 'finished' : 'active'}
            label=""
            hideLabel
          />
          <div style={{ display: 'flex', gap: '1rem', fontSize: '0.75rem', color: 'var(--cds-text-secondary)', marginTop: '0.2rem' }}>
            <span>Embedded: {job.embedded.toLocaleString()}</span>
            {job.failed > 0 && <span style={{ color: 'var(--cds-support-error)' }}>Failed: {job.failed}</span>}
            <span>/ {job.total.toLocaleString()}</span>
          </div>
        </div>
      ) : (
        <div style={{ flex: 1, color: 'var(--cds-text-secondary)', fontSize: '0.75rem' }}>
          {job.message ?? (job.status === 'running' ? 'Starting…' : '—')}
        </div>
      )}
      {job.completedAt && (
        <div style={{ flexShrink: 0, color: 'var(--cds-text-secondary)', fontSize: '0.75rem' }}>
          {new Date(job.completedAt).toLocaleTimeString()}
        </div>
      )}
    </div>
  );
}

// Made with Bob
